import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Video, Music, Scissors, Play, Pause, Type, Sticker, ArrowRight, Volume2, VolumeX, SkipBack, SkipForward } from 'lucide-react';
import Modal from './common/Modal';
import AudioSearchDialog from './common/AudioSearchDialog';
import PreviewPopup from './common/PreviewPopup';
import './VideoUploadPopup.css';

interface VideoUploadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

interface VideoData {
  file: File;
  url: string;
  thumbnail?: string;
  duration: number;
  // Editing data
  trimData?: { start: number; end: number };
  audioData?: { 
    url: string; 
    name: string; 
    volume: number;
    originalVolume: number;
    trimStart?: number;
    trimEnd?: number;
  };
  textOverlays?: any[];
  stickers?: any[];
  description: string;
}

type UploadStep = 'select' | 'edit' | 'details' | 'publish';
type EditMode = 'trim' | 'audio' | 'text' | 'sticker' | null;

const UPLOAD_STEPS: UploadStep[] = ['select', 'edit', 'details', 'publish'];

const VideoUploadPopup: React.FC<VideoUploadPopupProps> = ({ isOpen, onClose, onSubmit }) => {
  // Core state
  const [currentStep, setCurrentStep] = useState<UploadStep>('select');
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(null);
  
  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Video playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Edit tools state
  const [showAudioSearch, setShowAudioSearch] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Video details
  const [privacy, setPrivacy] = useState<'public' | 'friends' | 'private'>('public');
  const [location, setLocation] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    return () => {
      if (videoData?.url) {
        URL.revokeObjectURL(videoData.url);
      }
      if (videoData?.thumbnail) {
        URL.revokeObjectURL(videoData.thumbnail);
      }
    };
  }, []);

  useEffect(() => {
    const currentUrl = videoData?.url;
    const currentThumbnail = videoData?.thumbnail;
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      if (currentThumbnail) {
        URL.revokeObjectURL(currentThumbnail);
      }
    };
  }, [videoData?.url, videoData?.thumbnail]);

  // File handling
  const handleFileSelect = useCallback(async (files: FileList) => {
    const file = files[0];
    if (!file || !file.type.startsWith('video/')) return;

    console.log('📁 VideoUploadPopup handleFileSelect - file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      fileObject: file
    });

    setIsUploading(true);
    
    const videoUrl = URL.createObjectURL(file);
    const thumbnail = await generateVideoThumbnail(file);
    
    const newVideoData: VideoData = {
      file,
      url: videoUrl,
      thumbnail,
      duration: 0,
      description: '',
    };

    console.log('💾 VideoUploadPopup setVideoData - newVideoData:', {
      hasFile: !!newVideoData.file,
      fileName: newVideoData.file?.name,
      fileSize: newVideoData.file?.size,
      newVideoData
    });

    setVideoData(newVideoData);
    setCurrentStep('edit');
    setIsUploading(false);
  }, []);

  const generateVideoThumbnail = useCallback((file: File): Promise<string> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      video.currentTime = 1;
      video.onloadeddata = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(video, 0, 0);
        resolve(canvas.toDataURL());
        URL.revokeObjectURL(video.src);
      };
    });
  }, []);

  // Navigation
  const goToNextStep = useCallback(() => {
    const currentIndex = UPLOAD_STEPS.indexOf(currentStep);
    if (currentIndex < UPLOAD_STEPS.length - 1) {
      setCurrentStep(UPLOAD_STEPS[currentIndex + 1]);
      setEditMode(null);
    }
  }, [currentStep]);

  const goToPrevStep = useCallback(() => {
    const currentIndex = UPLOAD_STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(UPLOAD_STEPS[currentIndex - 1]);
      setEditMode(null);
    }
  }, [currentStep]);

  // Video controls
  const togglePlayback = useCallback(() => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      const videoDuration = videoRef.current.duration;
      setDuration(videoDuration);
      setVideoData(prev => prev ? { ...prev, duration: videoDuration } : null);
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  // Edit handlers
  const handleTrimChange = useCallback((start: number, end: number) => {
    if (!videoData) return;
    
    // Ensure valid trim values
    const validStart = Math.max(0, Math.min(start, duration));
    const validEnd = Math.max(validStart + 0.1, Math.min(end, duration));
    
    setVideoData(prev => prev ? {
      ...prev,
      trimData: { start: validStart, end: validEnd }
    } : null);
  }, [videoData, duration]);

  const handleAudioSelect = useCallback((audio: any) => {
    if (!videoData) return;
    
    const audioData = {
      url: audio.url || audio.audioUrl,
      name: audio.name || `${audio.title} - ${audio.artist}`,
      volume: 0.8,
      originalVolume: 0.2,
      trimStart: audio.trimStart,
      trimEnd: audio.trimEnd
    };
    
    setVideoData(prev => prev ? { ...prev, audioData } : null);
    setShowAudioSearch(false);
    setEditMode(null);
  }, [videoData]);

  // Drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  // Format time helper
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Reset form with proper cleanup
  const resetForm = useCallback(() => {
    // Cleanup object URLs before resetting
    if (videoData?.url) {
      URL.revokeObjectURL(videoData.url);
    }
    if (videoData?.thumbnail) {
      URL.revokeObjectURL(videoData.thumbnail);
    }
    
    setVideoData(null);
    setCurrentStep('select');
    setEditMode(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setPrivacy('public');
    setLocation('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [videoData]);

  // Handle video submission
  const handleSubmit = useCallback(async () => {
    if (!videoData) return;
    
    try {
      setIsUploading(true);
      
      // Debug logging to track file object
      console.log('🔍 VideoUploadPopup handleSubmit - videoData:', {
        hasFile: !!videoData.file,
        fileName: videoData.file?.name,
        fileSize: videoData.file?.size,
        fileType: videoData.file?.type,
        fileObject: videoData.file,
        description: videoData.description,
        duration: videoData.duration
      });
      
      // Prepare the submission data with the raw file for StatusBar to handle upload
      const submissionData = {
        file: videoData.file,
        description: videoData.description,
        privacy,
        location,
        duration: videoData.duration,
        thumbnail: videoData.thumbnail,
        audioData: videoData.audioData,
        trimData: videoData.trimData,
        textOverlays: videoData.textOverlays,
        stickers: videoData.stickers
      };
      
      console.log('🚀 VideoUploadPopup submissionData:', {
        hasFile: !!submissionData.file,
        fileName: submissionData.file?.name,
        fileSize: submissionData.file?.size,
        submissionData
      });
      
      // Call the parent's onSubmit function (StatusBar will handle the upload)
      await onSubmit(submissionData);
      
      // Close the popup and reset form
      setShowPreview(false);
      resetForm();
      onClose();
      
    } catch (error) {
      console.error('Error submitting video:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsUploading(false);
    }
  }, [videoData, privacy, location, onSubmit, onClose, resetForm]);

  // Get preview data for the preview popup
  const getPreviewData = useCallback(() => {
    if (!videoData) {
      return {
        description: '',
        privacy: 'public' as const,
        location: '',
        video: {
          preview: '',
          duration: 0,
          trimStart: 0,
          trimEnd: 0,
          audioData: undefined
        }
      };
    }
    
    return {
      description: videoData.description,
      privacy,
      location,
      video: {
        preview: videoData.url,
        duration: videoData.duration,
        trimStart: videoData.trimData?.start || 0,
        trimEnd: videoData.trimData?.end || videoData.duration,
        audioData: videoData.audioData
      }
    };
  }, [videoData, privacy, location]);

  // Helper functions
  const getStepTitle = () => {
    switch (currentStep) {
      case 'select': return 'Select Video';
      case 'edit': return 'Edit Video';
      case 'details': return 'Video Details';
      case 'publish': return 'Publish Video';
      default: return 'Upload Video';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'select': return videoData !== null;
      case 'edit': return true;
      case 'details': return true;
      default: return false;
    }
  };

  // Step components
  const renderSelectStep = () => (
    <div className="flex flex-col items-center justify-center h-96 space-y-6">
      <div 
        className={`w-full h-64 border-2 border-dashed rounded-xl flex flex-col items-center justify-center space-y-4 transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Video className="w-12 h-12 text-gray-400" />
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">Upload your video</p>
          <p className="text-sm text-gray-500">Drag and drop or click to select</p>
          <p className="text-xs text-gray-400 mt-2">MP4, MOV, AVI supported</p>
        </div>
      </div>
      
      <button
        onClick={() => fileInputRef.current?.click()}
        className="px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
      >
        Choose Video
      </button>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
        className="hidden"
      />
    </div>
  );

  const renderEditStep = () => (
    <div className="space-y-4">
      {/* Video Preview */}
      <div className="relative bg-black rounded-xl overflow-hidden aspect-video max-h-96">
        <video 
          ref={videoRef}
          src={videoData?.url} 
          className="w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onClick={(e) => e.stopPropagation()}
        />
        
        {/* Video Controls Overlay */}
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlayback}
        >
          <div className="bg-black/50 p-4 rounded-full transition-opacity hover:bg-black/70">
            {isPlaying ? (
              <Pause className="w-8 h-8 text-white" />
            ) : (
              <Play className="w-8 h-8 text-white ml-1" />
            )}
          </div>
        </div>
        
        {/* Time Display */}
        <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 text-white text-sm rounded-full">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>

      {/* Edit Tools */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button
          onClick={() => setEditMode('trim')}
          className={`p-4 rounded-xl border-2 transition-colors ${
            editMode === 'trim' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Scissors className="w-6 h-6 mx-auto mb-2" />
          <span className="text-sm font-medium">Trim</span>
        </button>
        
        <button
          onClick={() => setEditMode('audio')}
          className={`p-4 rounded-xl border-2 transition-colors ${
            editMode === 'audio' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Music className="w-6 h-6 mx-auto mb-2" />
          <span className="text-sm font-medium">Audio</span>
        </button>
        
        <button
          onClick={() => setEditMode('text')}
          className={`p-4 rounded-xl border-2 transition-colors ${
            editMode === 'text' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Type className="w-6 h-6 mx-auto mb-2" />
          <span className="text-sm font-medium">Text</span>
        </button>
        
        <button
          onClick={() => setEditMode('sticker')}
          className={`p-4 rounded-xl border-2 transition-colors ${
            editMode === 'sticker' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Sticker className="w-6 h-6 mx-auto mb-2" />
          <span className="text-sm font-medium">Stickers</span>
        </button>
      </div>

      {/* Edit Mode Panels */}
      {editMode === 'trim' && (
        <div className="p-4 bg-gray-50 rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Trim Video</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => seekTo(videoData?.trimData?.start || 0)}
                className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                title="Jump to start"
              >
                <SkipBack className="w-4 h-4" />
              </button>
              <button
                onClick={() => seekTo(videoData?.trimData?.end || duration)}
                className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                title="Jump to end"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Timeline Slider */}
          <div className="relative">
            <input
              type="range"
              min={0}
              max={duration}
              value={currentTime}
              onChange={(e) => seekTo(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            
            {/* Trim markers */}
            {videoData?.trimData && (
              <div className="absolute top-0 left-0 w-full h-2 pointer-events-none">
                <div 
                  className="absolute h-full bg-blue-500/30 rounded"
                  style={{
                    left: `${(videoData.trimData.start / duration) * 100}%`,
                    width: `${((videoData.trimData.end - videoData.trimData.start) / duration) * 100}%`
                  }}
                />
              </div>
            )}
          </div>
          
          {/* Trim inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input
                type="number"
                min={0}
                max={duration}
                step={0.1}
                value={videoData?.trimData?.start || 0}
                onChange={(e) => handleTrimChange(Number(e.target.value), videoData?.trimData?.end || duration)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input
                type="number"
                min={0}
                max={duration}
                step={0.1}
                value={videoData?.trimData?.end || duration}
                onChange={(e) => handleTrimChange(videoData?.trimData?.start || 0, Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {videoData?.trimData && (
            <div className="text-sm text-gray-600 text-center">
              Selected: {formatTime(videoData.trimData.end - videoData.trimData.start)} duration
            </div>
          )}
        </div>
      )}

      {editMode === 'audio' && (
        <div className="p-4 bg-gray-50 rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Audio Settings</h3>
            {videoData?.audioData && (
              <button
                onClick={() => setVideoData(prev => prev ? { ...prev, audioData: undefined } : null)}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Remove Audio
              </button>
            )}
          </div>
          
          <button
            onClick={() => setShowAudioSearch(true)}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <Music className="w-5 h-5" />
            <span>Browse Audio Tracks</span>
          </button>
          
          {videoData?.audioData && (
            <div className="space-y-3">
              <div className="p-3 bg-white rounded-lg border">
                <p className="font-medium text-gray-900">{videoData.audioData.name}</p>
                {videoData.audioData.trimStart !== undefined && videoData.audioData.trimEnd !== undefined && (
                  <p className="text-sm text-gray-600">
                    Trimmed: {formatTime(videoData.audioData.trimStart)} - {formatTime(videoData.audioData.trimEnd)}
                  </p>
                )}
              </div>
              
              {/* Volume controls */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Audio Volume</label>
                  <div className="flex items-center space-x-2">
                    <Volume2 className="w-4 h-4 text-gray-400" />
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.1}
                      value={videoData.audioData.volume}
                      onChange={(e) => setVideoData(prev => prev?.audioData ? {
                        ...prev,
                        audioData: { ...prev.audioData, volume: Number(e.target.value) }
                      } : prev)}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-600 w-8">{Math.round(videoData.audioData.volume * 100)}%</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Original Audio Volume</label>
                  <div className="flex items-center space-x-2">
                    <VolumeX className="w-4 h-4 text-gray-400" />
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.1}
                      value={videoData.audioData.originalVolume}
                      onChange={(e) => setVideoData(prev => prev?.audioData ? {
                        ...prev,
                        audioData: { ...prev.audioData, originalVolume: Number(e.target.value) }
                      } : prev)}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-600 w-8">{Math.round(videoData.audioData.originalVolume * 100)}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={videoData?.description || ''}
          onChange={(e) => setVideoData(prev => prev ? { ...prev, description: e.target.value } : null)}
          placeholder="Tell people about your video..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          rows={4}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Privacy
        </label>
        <select
          value={privacy}
          onChange={(e) => setPrivacy(e.target.value as 'public' | 'friends' | 'private')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="public">Public</option>
          <option value="friends">Friends Only</option>
          <option value="private">Private</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Location (Optional)
        </label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Add location..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Video preview */}
      <div className="p-4 bg-gray-50 rounded-xl">
        <h3 className="font-medium text-gray-900 mb-3">Preview</h3>
        <div className="flex space-x-3">
          <img 
            src={videoData?.thumbnail} 
            alt="Video thumbnail"
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{videoData?.file.name}</p>
            <p className="text-sm text-gray-600">Duration: {formatTime(videoData?.duration || 0)}</p>
            {videoData?.trimData && (
              <p className="text-sm text-blue-600">
                Trimmed to {formatTime(videoData.trimData.end - videoData.trimData.start)}
              </p>
            )}
            {videoData?.audioData && (
              <p className="text-sm text-green-600">Custom audio added</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={getStepTitle()}
        size="lg"
        footer={
          <div className="flex justify-between items-center w-full">
            <button
              onClick={currentStep === 'select' ? onClose : goToPrevStep}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {currentStep === 'select' ? 'Cancel' : 'Back'}
            </button>
            
            {/* Progress indicator */}
            <div className="flex space-x-2">
              {UPLOAD_STEPS.map((step, index) => (
                <div
                  key={step}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    UPLOAD_STEPS.indexOf(currentStep) >= index
                      ? 'bg-blue-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={currentStep === 'details' ? () => setShowPreview(true) : goToNextStep}
              disabled={!canProceed() || isUploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <span>{currentStep === 'details' ? 'Preview' : 'Next'}</span>
              {currentStep !== 'details' && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        }
      >
        <div className="min-h-96">
          {isUploading && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Processing your video...</p>
              </div>
            </div>
          )}
          
          {!isUploading && (
            <>
              {currentStep === 'select' && renderSelectStep()}
              {currentStep === 'edit' && renderEditStep()}
              {currentStep === 'details' && renderDetailsStep()}
            </>
          )}
        </div>
      </Modal>

      {/* Audio Search Dialog */}
      {showAudioSearch && (
        <AudioSearchDialog
          isOpen={showAudioSearch}
          onClose={() => setShowAudioSearch(false)}
          onSelectAudio={handleAudioSelect}
        />
      )}

      {/* Preview Popup */}
      {showPreview && (
        <PreviewPopup
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          onConfirm={handleSubmit}
          type="video"
          data={getPreviewData()}
        />
      )}
    </>
  );
};

export default VideoUploadPopup;
